<template>
    <section>

        <ContentLayer class="slide background"
            v-if="slide.background"
            :settings="settings"
            :content="slide" :level="'slide'" :layer="'background'"
            :structure="structure" :navigationData="navigationData" :active="active"
            :key="'keyframe-' + chapter.uri + '-' + slide.uri + '-background'" />

        <ContentLayer class="slide content"
            ref="content"
            v-if="slide.content"
            :settings="settings"
            :content="slide" :level="'slide'" :layer="'content'"
            :structure="structure" :navigationData="navigationData" :active="active"
            :key="'content-' + chapter.uri + '-' + slide.uri + '-content'" />

        <section v-if="slide.keyframes && slide.keyframes.length" class="slide-keyframes fullscreen">

            <Keyframe class="keyframe previous fullscreen"
                v-if="renderPrevious"
                :settings="settings"
                :navigationData="navigationData"
                :keyframeIndex="previousIndex"
                :slideIndex="slideIndex"
                :chapterIndex="chapterIndex"
                :presentationIndex="presentationIndex"
                :chapter="chapter"
                :slide="slide"
                :structure="structure"
                :animating="keyframeAnimating"
                :key="keyPrevious" />

            <Keyframe class="keyframe current fullscreen"
                v-if="renderCurrent"
                :settings="settings"
                :navigationData="navigationData"
                :keyframeIndex="currentIndex"
                :slideIndex="slideIndex"
                :chapterIndex="chapterIndex"
                :presentationIndex="presentationIndex"
                :chapter="chapter"
                :slide="slide"
                :structure="structure"
                :animating="keyframeAnimating"
                :key="keyCurrent" />

            <Keyframe class="keyframe next fullscreen"
                v-if="renderNext"
                :settings="settings"
                :navigationData="navigationData"
                :keyframeIndex="nextIndex"
                :slideIndex="slideIndex"
                :chapterIndex="chapterIndex"
                :presentationIndex="presentationIndex"
                :chapter="chapter"
                :slide="slide"
                :structure="structure"
                :animating="keyframeAnimating"
                :key="keyNext" />

        </section>

        <ContentLayer class="slide foreground"
            v-if="slide.foreground"
            :settings="settings"
            :content="slide" :level="'slide'" :layer="'foreground'"
            :structure="structure" :navigationData="navigationData" :active="active"
            :key="'keyframe-' + chapter.uri + '-' + slide.uri + '-foreground'" />

    </section>
</template>

<script>
import Keyframe from '@/core/components/Keyframe'
import navigationMixin from '@/core/mixins/navigationMixin.js'
import { TweenLite, Power1, Power2, Power3 } from 'gsap'

export default {
    data: () => {
        return {
            keyframeAnimating: false,
            previousIndex: false,
            currentIndex: false,
            nextIndex: false,
            animationForward: false,
            animationBackward: false,
            previousKeyframeIndex: false,
            previousSlideIndex: false
        }
    },
    props: ['settings', 'presentationIndex', 'slideIndex', 'chapterIndex', 'animating', 'chapter'],
    watch: {
        active(val) {
            if (val) this.onActivated()
        },
        navigationData(data) {

            // kill animations
            if (this.animationForward) {
                this.animationForward.progress(1).kill()
                this.animationForward = false
            }

            if (this.animationBackward) {
                this.animationBackward.progress(1).kill()
                this.animationBackward = false
            }

            if (this.active) {

                if (this.previousSlideIndex !== this.currentSlideIndex) {
                    this.onAnimationReady(this.currentKeyframeIndex)
                } else {

                    // animate forward
                    if (this.currentKeyframeIndex > this.previousKeyframeIndex) {
                        this.nextIndex = this.currentKeyframeIndex
                        this.previousIndex = false
                        if (this.keyframeTransition) {
                            this.slideForward(this.currentKeyframeIndex)
                        } else {
                            this.onAnimationReady(this.currentKeyframeIndex)
                        }
                    }

                    // animate backward
                    if (this.currentKeyframeIndex < this.previousKeyframeIndex) {
                        this.previousIndex = this.currentKeyframeIndex
                        this.nextIndex = false
                        if (this.keyframeTransition) {
                            this.slideBackward(this.currentKeyframeIndex)
                        } else {
                            this.onAnimationReady(this.currentKeyframeIndex)
                        }
                    }

                }
            }

            this.previousSlideIndex = this.currentSlideIndex

        }
    },
    mixins: [navigationMixin],
    computed: {
        keyframeTransition() {
            if (this.slide.keyframeTransition) {
                return this.slide.keyframeTransition
            } else {
                return this.settings.keyframeTransition
            }
        },
        active() {
            return this.presentationIndex === this.currentPresentationIndex &&
                this.chapterIndex === this.currentChapterIndex &&
                this.slideIndex === this.currentSlideIndex
        },
        renderPrevious() {
            if (this.previousIndex === false) return false
            if (!this.keyframes[this.previousIndex]) return false
            return this.keyframeAnimating;
        },
        renderCurrent() {
            if (this.currentIndex === false) return false
            if (!this.keyframes[this.currentIndex]) return false
            return true
        },
        renderNext() {
            if (this.nextIndex === false) return false
            if (!this.keyframes[this.nextIndex]) return false
            return true
        },
        keyPrevious() {
            if (!this.renderPrevious) return 'keyframe-previous-none'
            return 'keyframe-' + this.chapter.uri + '-' + this.slide.uri + '-' + this.keyframes[this.previousIndex].uri
        },
        keyCurrent() {
            if (!this.renderCurrent) return 'keyframe-current-none'
            return 'keyframe-' + this.chapter.uri + '-' + this.slide.uri + '-' + this.keyframes[this.currentIndex].uri
        },
        keyNext() {
            if (!this.renderNext) return 'keyframe-next-none'
            return 'keyframe-' + this.chapter.uri + '-' + this.slide.uri + '-' + this.keyframes[this.nextIndex].uri
        },
        slide() {
            return this.chapter.slides[this.slideIndex]
        },
        keyframes() {
            return this.slide.keyframes
        },
        currentKeyframe() {
            return this.keyframes[this.currentKeyframeIndex]
        }
    },
    methods: {
        onActivated() {
            if (!this.settings.disableNavigationDebugLogging) console.log("slide: ", this.slide.uri)
        },
        initializeSlide() {
            this.previousSlideIndex = this.currentSlideIndex
            this.onAnimationReady(this.currentKeyframeIndex)
            this.$el.style.backgroundColor = this.settings.slideBackgroundColor
        },
        slideForward(index) {
            this.keyframeAnimating = true
            this.animationForward = TweenLite.fromTo(".slide-keyframes", {x: "0%"}, {duration: this.settings.slideTransitionSpeed, ease: Power1.easeInOut, x: "-100%", onComplete: () => {
                this.onAnimationReady(index)
            }})
        },
        slideBackward(index) {
            this.keyframeAnimating = true
            this.animationBackward = TweenLite.fromTo(".slide-keyframes", {x: "0%"}, {duration: this.settings.slideTransitionSpeed, ease: Power1.easeInOut, x: "100%", onComplete: () => {
                this.onAnimationReady(index)
            }})
        },
        onAnimationReady(index) {
            let container = this.$el.querySelector(".slide-keyframes")
            if (container) {
                container.style.transform = 'translateX(0)'
            }

            this.previousKeyframeIndex = index;

            if (!this.slide) {
                this.keyframeAnimating = false
                return
            }
            this.keyframeAnimating = false

            this.currentIndex = index

            if (this.currentIndex > 0) {
                this.previousIndex = this.currentIndex - 1
            } else {
                this.previousIndex = false
            }

            if (this.currentIndex < this.keyframes.length - 1) {
                this.nextIndex = this.currentIndex + 1
            } else {
                this.nextIndex = false
            }
        }
    },
    components: {
        Keyframe
    },
    created() {},
    mounted() {
        this.initializeSlide()
    }
}
</script>

<style scoped lang="scss">

    @import '~@/core/assets/scss/_variables.scss';

    .slide.background {
        z-index: 0;
    }

    .slide.content {
        z-index: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        overflow: hidden;
    }

    .slide-keyframes {
        z-index: 2;

        .keyframe {

            &.current {
                transform: translate(0, 0);
                z-index: 2;
            }

            &.previous {
                transform: translate(-100%, 0);
                z-index: 1;
            }

            &.next {
                transform: translate(100%, 0);
                z-index: 1;
            }

        }

    }

    .slide.foreground {
        z-index: 3;
        pointer-events: none;
    }

</style>